import AuthService from '@root/core/src/services/auth-service';
import PropTypes from '@root/vendor/prop-types';
import React, { useCallback, useEffect, useRef, useState } from '@root/vendor/react';
import SceneLoader from '@root/core/src/components/scene-loader';
import environment from '@root/core/src/utils/environment';
import useAccessToken from '@root/agents.joinroot.com/src/services/use-access-token';
import { expireSessionToken, sessionContext, validateSessionToken } from '@root/agents.joinroot.com/src/api/root-server';
import { useAgentDispatch } from '@root/agents.joinroot.com/src/contexts/agent-context';
import { useHistory } from '@root/vendor/react-router-dom';
import { useLogger } from '@root/agents.joinroot.com/src/utils/logging';

export default function AuthController({
  ProtectedApp,
  PublicRouter,
}) {
  const logger = useLogger();
  const history = useHistory();
  const agentDispatch = useAgentDispatch();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loggingIn, setIsLoggingIn] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const { setAccessToken } = useAccessToken();
  const redirectOnLoginPath = useRef();
  const quoteIdForRedirect = useRef();
  const quoteSourceForRedirect = useRef();
  const quoteExperienceForRedirect = useRef();

  useEffect(() => {
    const searchParams = new URLSearchParams(history.location.search);

    const trimmedPath = history.location.pathname.replace(/\/$/, '');
    if (trimmedPath === '/authenticate' && searchParams.get('quote_id')) {
      quoteIdForRedirect.current = searchParams.get('quote_id');
    }

    if (trimmedPath === '/authenticate' && searchParams.get('quote_source')) {
      quoteSourceForRedirect.current = searchParams.get('quote_source');
    }

    if (trimmedPath === '/authenticate' && searchParams.get('experience')) {
      quoteExperienceForRedirect.current = searchParams.get('experience');
    }

    if (!isAuthenticated && !redirectOnLoginPath.current && searchParams.get('redirectOnLogin')) {
      redirectOnLoginPath.current = history.location.pathname;
    }
  }, [isAuthenticated, history.location]);

  const onLogin = (token) => {
    setIsLoggingIn(true);
    setAccessToken(token);
  };

  useEffect(() => {
    validateSessionToken()
      .then((data) => {
        const searchParams = new URLSearchParams(history.location.search);

        setIsAuthenticated(data?.valid || false);

        if (searchParams.get('user_id')) {
          redirectOnLoginPath.current = {
            pathname: history.location.pathname,
            state: {
              userId: searchParams.get('user_id') || null,
            },
          };
        }
      })
      .catch(() => setIsAuthenticated(false))
      .finally(() => { setIsValidating(false); });
  }, [history.location.pathname, history.location.search, loggingIn]);

  useEffect(() => {
    if (isValidating) { return; }

    if (!isAuthenticated) {
      agentDispatch({
        type: 'clear',
      });
      return;
    }

    sessionContext()
      .then((data) => {
        AuthService.setCurrentUserContext({
          userId: data.agent?.id,
        });
        agentDispatch({
          type: 'set',
          payload: data['agent'],
        });
      })
      .catch((error) => {
        logger.info('INVALID_SESSION_CONTEXT', {
          error,
        });
        history.push('/login');
      });
  }, [logger, agentDispatch, history, history.location.pathname, isAuthenticated, isValidating]);

  const postExpireSessionToken = useCallback(async () => {
    await expireSessionToken();
  }, []);

  const passProps = {
    onLogin: () => {
      setIsLoggingIn(true);
    },
    onLogout: () => {
      postExpireSessionToken();
      AuthService.clear();
      AuthService.clearCache(AuthService.REMEMBER_DEVICE_TOKEN);
      setIsAuthenticated(false);
    },
  };

  if (isValidating) {
    return <SceneLoader hideHeader={true} />;
  }

  if (isAuthenticated) {
    return (
      <ProtectedApp
        inactiveTimeout={environment.inactiveTimeout}
        onLogout={passProps.onLogout}
        quoteExperienceForRedirect={quoteExperienceForRedirect.current}
        quoteIdForRedirect={quoteIdForRedirect.current}
        quoteSourceForRedirect={quoteSourceForRedirect.current}
        redirectOnLoginPath={redirectOnLoginPath.current}
      />
    );
  }

  return (
    <PublicRouter
      setAccessToken={onLogin}
    />);
}

AuthController.propTypes = {
  ProtectedApp: PropTypes.func.isRequired,
  PublicRouter: PropTypes.func.isRequired,
};
